import {
  Box,
  CircularProgress,
  IconButton,
  Slider,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { FC, useContext, useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { formatDateByLocale } from '../../../utils/timeRelatedFunctions';
import ArrowCircleLeftOutlinedIcon from '@mui/icons-material/ArrowCircleLeftOutlined';
import ArrowCircleRightOutlinedIcon from '@mui/icons-material/ArrowCircleRightOutlined';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import useSettingModal from '../hooks/useSettingModal';
import { authType } from '../../../features/client/types';
import { useLoaderData } from 'react-router-dom';
import useFetchUser from '../../../features/client/hooks/useFetchUser';
import usePaymentModal from '../hooks/usePaymentModal';
import { HOUR_MS, findSliderMaxMin } from '../functions/forecastFrames';
import PredicionMapContext from '../preditionMapContext';
import { Tutorial } from '../../../Tutorial';
import { sendAnalyticsEvent } from '../../../utils/googleAnalytics';

// データがない場合
const TUTORIAL_NO_DATA = [
  {
    target: '#chooseCalculatedTime',
    content: <Typography sx={{ whiteSpace: 'pre-line' }}>{`時刻をクリックすることで、過去の洪水予測を確認することができます。\n（予測の初期時刻を設定可能）`}</Typography>,
    locale: {
      skip: 'スキップ',
      next: '次へ',
    },
    disableBeacon: true,
    disableOverlayClose: true,
    spotlightClicks: true,
  },
  {
    target: '#useAddressInput',
    content: <Typography sx={{ whiteSpace: 'pre-line' }}>{`日本全国の住所や施設名を入力して、その周辺の洪水浸水を確認できます。`}</Typography>,
    locale: {
      skip: 'スキップ',
      next: '次へ',
      back: '戻る',
      last: '終了',
    },
  },
  // 共通
  {
    target: '#commonMenuNewAlert',
    content: <Typography sx={{ whiteSpace: 'pre-line' }}>{`洪水予測時にメール等での通知を行う対象地点/エリアを登録できます。\n（契約ユーザのみの機能）`}</Typography>,
    locale: {
      skip: 'スキップ',
      next: '次へ',
      back: '戻る',
      last: '終了',
    },
  },
  {
    target: '#commonMenuHelp',
    content: <Typography sx={{ whiteSpace: 'pre-line' }}>{`チュートリアルは以上です。\nヘルプより、本アプリの使い方の詳細や、分析手法の確認や、再度本チュートリアルの実施が可能です。\nご不明点あれば、app@gaia-vision.co.jpまでお問い合わせください`}</Typography>,
    locale: {
      back: '戻る',
      next: '終了',
      last: '終了'
    },
  },
];

// データがある場合
const TUTORIAL_EXIST_DATA = [
  {
    target: '#timeSelectSlider',
    content: <Typography sx={{ whiteSpace: 'pre-line' }}>{`時間をスライドさせることで、約1日半先までの洪水浸水予測を確認できます。\n（契約ユーザのみの機能）`}</Typography>,
    locale: {
      skip: 'スキップ',
      next: '次へ',
    },
    disableBeacon: true,
    disableOverlayClose: true,
    spotlightClicks: true,
  },
  {
    target: '#timeSelectSliderDesc',
    content: <Typography sx={{ whiteSpace: 'pre-line' }}>{`時刻をクリックすることで、過去の洪水予測を確認できます。\n※予測の初期時刻を設定可能です。近年のもの/大きな洪水発生時のものが掲載されています（黒字時刻が選択可能）`}</Typography>,
    locale: {
      skip: 'スキップ',
      next: '次へ',
      back: '戻る',
      last: '終了',
    },
  },
  // 共通
  {
    target: '#commonMenuNewAlert',
    content: <Typography sx={{ whiteSpace: 'pre-line' }}>{`洪水予測時にメール等での通知を行う対象地点/エリアを登録できます。\n（契約ユーザのみの機能）`}</Typography>,
    locale: {
      skip: 'スキップ',
      next: '次へ',
      back: '戻る',
      last: '終了',
    },
  },
  {
    target: '#commonMenuHelp',
    content: <Typography sx={{ whiteSpace: 'pre-line' }}>{`チュートリアルは以上です。\nヘルプより、本アプリの使い方の詳細や、分析手法の確認や、再度本チュートリアルの実施が可能です。\nご不明点あれば、app@gaia-vision.co.jpまでお問い合わせください`}</Typography>,
    locale: {
      back: '戻る',
      next: '終了',
      last: '終了',
    },
  },
];

type Props = {
  setIncrement: React.Dispatch<React.SetStateAction<number>>;
  increment: number;
  isImageIdChecking: boolean;
};

// MUIの <Slider /> コンポーネントは制御用のvalueをpropsで渡すとスムーズに動かないのでこのようにした.

const TimeSelectSlider: FC<Props> = ({
  increment,
  setIncrement,
  isImageIdChecking,
}) => {
  const authInfo = useLoaderData() as authType;
  const { isPaid } = useFetchUser(authInfo.user.cognito_username);
  const { t, i18n } = useTranslation();
  // FIXME: Slider用に用意しないといけないので仕方なく用意した. 冗長なのでなくしたい.
  const {
    sliderValue,
    setSliderValue,
    calculatedTime,
    setCalculatedTime,
    imageIdList,
    sliderMarks,
    selectedFrame,
    displayedValidTimeMs,
    requestFrame,
    retrySelected,
  } = useContext(PredicionMapContext);
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const { togglePaymentModal, PaymentModal } = usePaymentModal('map');
  const { toggleModalShow, Modal } = useSettingModal({
    increment,
    setIncrement,
    setCalculatedTime,
    calculatedTime,
  });

  const [isTutorial, setIsTutorial] = useState<boolean>(localStorage.getItem('tutorialStatus') !== 'finished');

  const handleOpenSettingModal = () => {
    // GA4: 起算時刻・時間間隔設定モーダルの表示を計測
    sendAnalyticsEvent('prediction_time_setting', {
      action: 'open',
      current_increment_hours: increment,
    });
    toggleModalShow();
  };

  useEffect(() => {
    const start = (event: StorageEvent) => {
      setIsTutorial(true);
    };

    const end = (event: StorageEvent) => {
      setIsTutorial(false);
    };

    // @ts-expect-error イベント追加しているので除外
    window.addEventListener('tutorial-start', start);
    // @ts-expect-error イベント追加しているので除外
    window.addEventListener('tutorial-end', end);
    return () => {
      // @ts-expect-error イベント追加しているので除外
      window.removeEventListener('tutorial-start', start);
      // @ts-expect-error イベント追加しているので除外
      window.removeEventListener('tutorial-start', end);
    };
  }, []);

  // 初期値と目盛りは起算時刻が決まったときに親（PredictionMapPage）で決める.
  const { sliderMax, sliderMin } = findSliderMaxMin(sliderMarks);

  const loopTimeIdx = (prevIdx: number) => {
    if (prevIdx >= sliderMax) {
      return sliderMin;
    }
    return Math.min(prevIdx + increment, sliderMax);
  };

  // 再生は1秒ごとに次の時刻へ進む. 次の時刻の URL がまだ無ければ進まずに待ち、
  // 前の地図に新しい時刻のラベルを付けないようにする.
  const playbackRef = useRef({ sliderValue, calculatedTime, loopTimeIdx, requestFrame });
  playbackRef.current = { sliderValue, calculatedTime, loopTimeIdx, requestFrame };
  useEffect(() => {
    if (!isPlaying) {
      return;
    }
    const timer = setInterval(() => {
      const playback = playbackRef.current;
      if (!playback.calculatedTime) {
        return;
      }
      const nextValue = playback.loopTimeIdx(playback.sliderValue);
      const nextValidTimeMs = playback.calculatedTime.getTime() + nextValue * HOUR_MS;
      const status = playback.requestFrame(nextValidTimeMs);
      if (status === 'idle' || status === 'loading') {
        return;
      }
      setSliderValue(nextValue);
      // 取り直しても失敗した時刻ではそこで止め、エラーと再試行を見せる.
      if (status === 'error') {
        setIsPlaying(false);
      }
    }, 1000); // 1000ミリ秒 = 1秒
    return () => clearInterval(timer);
  }, [isPlaying, setSliderValue]);

  if (!!isImageIdChecking) {
    return (
      <>
        <Stack direction={'column'} textAlign={'center'}>
          <Typography>{t('pages.predictionMap.fetching')}</Typography>
          <Box style={{ width: '100%', marginTop: '12px' }}>
            <CircularProgress />
          </Box>
        </Stack>
      </>
    );
  }

  // imageIDがなかった場合に相当. TIFが保存されていない場合など.
  if (!calculatedTime || imageIdList.length < 1) {
    return (
      <>
        {Modal}
        {PaymentModal}
        <Tutorial steps={TUTORIAL_NO_DATA} isRunning={isTutorial} />
        <Typography
          id="chooseCalculatedTime"
          style={{
            fontSize: '8pt',
            color: '#68a4d9',
            cursor: 'pointer',
            textDecoration: 'underline',
            display: 'inline-block',
          }}
          onClick={handleOpenSettingModal}
        >
          {t('pages.predictionMap.chooseCalculatedTime')}
        </Typography>
        <Stack direction={'row'} textAlign={'left'} style={{ maxWidth: '300px', margin: '8px auto 0' }}>
          <Tooltip title={<pre>{t('pages.predictionMap.contact')}</pre>}>
            <ReportProblemIcon style={{ fontSize: '12pt', marginRight: '10px', color: 'red' }} />
          </Tooltip>
          <Typography style={{ fontSize: '10pt' }}>
            {t('pages.predictionMap.loadFailed')}
          </Typography>
        </Stack>
      </>
    );
  }
  const targetTime = new Date(calculatedTime);
  targetTime.setHours(targetTime.getHours() + sliderValue);

  const formattedTime = formatDateByLocale(targetTime, i18n.language);

  const handleDecrease = () => {
    const canDecrease = sliderValue - increment >= sliderMin;
    if (canDecrease) {
      // GA4: 予測時刻を戻す操作を計測
      sendAnalyticsEvent('prediction_time_change', {
        method: 'decrease_button',
        target_offset_hours: sliderValue - increment,
      });
    }
    setSliderValue(prevValue => (canDecrease ? prevValue - increment : prevValue));
  };

  const handleIncrease = () => {
    if (!isPaid && targetTime.setHours(targetTime.getHours() + increment) > new Date().getTime()) {
      togglePaymentModal();
      return;
    }
    const canIncrease = sliderValue + increment <= sliderMax;
    if (canIncrease) {
      // GA4: 予測時刻を進める操作を計測
      sendAnalyticsEvent('prediction_time_change', {
        method: 'increase_button',
        target_offset_hours: sliderValue + increment,
      });
    }
    setSliderValue(canIncrease ? sliderValue + increment : sliderValue);
  };

  // sliderValueを基準に incrementずつしか動かないようにする.
  const handleChange = (_: Event, newValue: number | number[]) => {
    let _value = newValue as number;
    if (_value < sliderMin) {
      return;
    }

    // 有償版
    const nextTargetTime = new Date(calculatedTime);
    nextTargetTime.setHours(nextTargetTime.getHours() + _value);
    if (!isPaid && nextTargetTime > new Date()) {
      togglePaymentModal();
      return;
    }
    const nextValue = Math.floor((_value - sliderValue) / increment) * increment + sliderValue;
    if (nextValue !== sliderValue) {
      // GA4: スライダーでの予測時刻変更を計測
      sendAnalyticsEvent('prediction_time_change', {
        method: 'slider',
        target_offset_hours: nextValue,
      });
    }
    setSliderValue(nextValue as number);
    return;
  };

  const handleStop = () => {
    // GA4: 予測時刻タイムラインの再生停止を計測
    sendAnalyticsEvent('prediction_time_playback', { action: 'stop' });
    setIsPlaying(false);
  };

  const handleStart = () => {
    // GA4: 予測時刻タイムラインの再生開始を計測
    sendAnalyticsEvent('prediction_time_playback', { action: 'start' });
    setIsPlaying(true);
  };

  // 選択中の時刻の地図がまだ出ていないときの表示. スライダーは他の時刻を選べるように残す.
  const selectedStatus = selectedFrame?.status;
  const isSelectedWaiting = selectedStatus === 'idle' || selectedStatus === 'loading';
  const isSelectedError = selectedStatus === 'error';
  const displayedTimeLabel =
    displayedValidTimeMs !== null && displayedValidTimeMs !== selectedFrame?.validTimeMs
      ? formatDateByLocale(new Date(displayedValidTimeMs), i18n.language)
      : null;
  // 最新の解析が取れず古い解析で代わりに表示しているときは、どの解析かを見せる.
  const fallbackOriginMs = selectedFrame?.fallbackOriginMs ?? null;
  const fallbackLabel =
    fallbackOriginMs !== null ? formatDateByLocale(new Date(fallbackOriginMs), i18n.language) : null;

  return (
    <>
      <Tutorial steps={TUTORIAL_EXIST_DATA} isRunning={isTutorial} />
      <Box style={{ textAlign: 'center' }}>
        {Modal}
        {PaymentModal}
        <Box style={{ display: 'flex' }}>
          <Box style={{ backgroundColor: '#a0d8ef', padding: '4px 0px 4px 0px', width: '90%' }}>
            <Typography style={{ fontSize: '10pt' }}>
              {t('pages.predictionMap.timestamp', { date: formattedTime })}
            </Typography>
          </Box>
          <Box style={{ width: '15%', padding: '1px', marginLeft: '-4px' }}>
            <div style={{ borderRadius: '32x', border: 'solid #ddd 1px', backgroundColor: '#eee' }}>
              {(isPlaying && (
                <IconButton
                  onClick={handleStop}
                  size="small"
                  sx={{ padding: '0px', marginTop: '0px' }}
                >
                  <PauseIcon sx={{ fontSize: 20, color: 'green' }} />
                </IconButton>
              )) || (
                <IconButton
                  onClick={handleStart}
                  size="small"
                  sx={{ padding: '0px', marginTop: '0px' }}
                >
                  <PlayArrowIcon sx={{ fontSize: 20, color: 'green' }} />
                </IconButton>
              )}
            </div>
          </Box>
        </Box>

        {(isSelectedWaiting || isSelectedError) && (
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            justifyContent="center"
            role="status"
            sx={{ marginTop: '4px' }}
          >
            {isSelectedWaiting && <CircularProgress size={12} />}
            {isSelectedError && <ReportProblemIcon style={{ fontSize: '11pt', color: 'red' }} />}
            <Typography style={{ fontSize: '8pt' }}>
              {isSelectedWaiting ? t('pages.predictionMap.frameLoading') : t('pages.predictionMap.frameError')}
              {displayedTimeLabel && t('pages.predictionMap.frameDisplaying', { date: displayedTimeLabel })}
            </Typography>
            {isSelectedError && (
              <Typography
                component="span"
                onClick={retrySelected}
                style={{ fontSize: '8pt', color: '#68a4d9', cursor: 'pointer', textDecoration: 'underline' }}
              >
                {t('pages.predictionMap.frameRetry')}
              </Typography>
            )}
          </Stack>
        )}

        {selectedStatus === 'ready' && fallbackLabel && (
          <Typography role="status" style={{ fontSize: '8pt', marginTop: '4px' }}>
            {t('pages.predictionMap.frameFallback', { date: fallbackLabel })}
          </Typography>
        )}

        <Box style={{ textAlign: 'left', padding: '6px 0px 0px 6px' }}  id="timeSelectSliderDesc">

          <Typography style={{ fontSize: '9pt' }}>
            <Trans
              i18nKey="pages.predictionMap.calculatedAt"
              components={[
                <span
                  onClick={handleOpenSettingModal}
                  style={{ color: '#68a4d9', cursor: 'pointer', textDecoration: 'underline' }}
                />,
              ]}
              values={{ date: formatDateByLocale(calculatedTime, i18n.language) }}
            />
          </Typography>

          <Typography style={{ fontSize: '9pt', marginTop: '3px' }}>
            <Trans
              i18nKey="pages.predictionMap.timeInterval"
              components={[
                <span
                  onClick={handleOpenSettingModal}
                  style={{ color: '#68a4d9', cursor: 'pointer', textDecoration: 'underline' }}
                />,
              ]}
              values={{ interval: increment }}
            />
          </Typography>
        </Box>

        <Box id="timeSelectSlider">
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              margin: '12px 0px 16px 0px',
            }}
          >
            <IconButton
              onClick={handleDecrease}
              size="small"
              sx={{ padding: '0px', marginTop: '-18px' }}
              disabled={isPlaying}
            >
              <ArrowCircleLeftOutlinedIcon sx={{ fontSize: 20 }} />
            </IconButton>
            <Slider
              value={sliderValue}
              onChange={handleChange}
              step={1}
              marks={sliderMarks}
              min={sliderMin - 1}
              max={sliderMax}
              track={false}
              disabled={isPlaying}
              sx={{
                '& .MuiSlider-markLabel': {
                  fontSize: '0.68rem', // フォントサイズを小さく設定
                },
              }}
            />
            <IconButton
              onClick={handleIncrease}
              size="small"
              sx={{ padding: '0px', marginTop: '-18px' }}
              disabled={isPlaying}
            >
              <ArrowCircleRightOutlinedIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default TimeSelectSlider;
